// Validation utility functions

// Email validation
export const validateEmail = (email: string): string => {
  if (!email) return 'Email is required';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return 'Please enter a valid email address';
  }
  return '';
};

// URL validation
export const validateUrl = (url: string, fieldName: string): string => {
  if (!url) return `${fieldName} is required`;
  try {
    new URL(url);
    return '';
  } catch {
    return 'Please enter a valid URL (e.g., https://example.com)';
  }
};

// Required field validation
export const validateRequired = (value: string, fieldName: string): string => {
  if (!value || value.trim() === '') {
    return `${fieldName} is required`;
  }
  return '';
};

// Name validation (letters, spaces, hyphens, numbers)
export const validateName = (name: string, fieldName: string): string => {
  if (!name) return `${fieldName} is required`;
  if (name.length < 2) return `${fieldName} must be at least 2 characters`;
  if (name.length > 50) return `${fieldName} must be less than 50 characters`;
  if (!/^[a-zA-Z0-9\s\-]+$/.test(name)) {
    return `${fieldName} can only contain letters, numbers, spaces, and hyphens`;
  }
  return '';
};

// Code validation (lowercase letters only, no spaces)
export const validateCode = (code: string, fieldName: string): string => {
  if (!code) return `${fieldName} is required`;
  if (code.length < 2) return `${fieldName} must be at least 2 characters`;
  if (code.length > 50) return `${fieldName} must be less than 50 characters`;
  if (!/^[a-z]+$/.test(code)) {
    return `${fieldName} can only contain lowercase letters`;
  }
  return '';
};

// UUID validation
export const validateUuid = (uuid: string, fieldName: string): string => {
  if (!uuid) return `${fieldName} is required`;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(uuid)) {
    return `${fieldName} must be a valid UUID format`;
  }
  return '';
};

// Number validation
export const validateNumber = (value: number, fieldName: string, min: number = 0): string => {
  if (value === undefined || value === null) return `${fieldName} is required`;
  if (isNaN(value)) return `${fieldName} must be a valid number`;
  if (value < min) return `${fieldName} must be at least ${min}`;
  return '';
};

// Date validation
export const validateDate = (date: Date | null, fieldName: string): string => {
  if (!date) return `${fieldName} is required`;
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return `${fieldName} must be a valid date`;
  }
  return '';
};

// Date range validation
export const validateDateRange = (startDate: Date | null, endDate: Date | null): string => {
  if (!startDate || !endDate) return 'Both start and end dates are required';
  if (startDate >= endDate) {
    return 'End date must be after start date';
  }
  return '';
};

// Array validation
export const validateArray = (array: any[], fieldName: string, minItems: number = 1): string => {
  if (!Array.isArray(array)) return `${fieldName} must be an array`;
  if (array.length < minItems) return `${fieldName} must have at least ${minItems} item(s)`;
  return '';
};

// PIN validation (6 digits)
export const validatePin = (pin: string): string => {
  if (!pin) return 'PIN is required';
  if (pin.length !== 6) return 'PIN must be exactly 6 digits';
  if (!/^\d{6}$/.test(pin)) return 'PIN must contain only numbers';
  return '';
};

// Status validation
export const validateStatus = (status: string, validStatuses: string[], fieldName: string): string => {
  if (!status) return `${fieldName} is required`;
  if (!validStatuses.includes(status)) {
    return `${fieldName} must be one of: ${validStatuses.join(', ')}`;
  }
  return '';
}; 